import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  PreScheduleCardProps,
  PreScheduleCardTagLabel,
} from './PreScheduleCard.types'
import { DateTime } from 'luxon'
import { autoCapitalize } from '@conexasaude/front-utils/formatters'
import { useModalContext } from '@/components/Modal/contexts/ModalContext'
import { PRE_SCHEDULE_CONFIRMATION_CARD_MODAL_ID } from '../modals/CancelPreScheduleConfirmationModal/CancelPreScheduleConfirmationModal.constants'
import {
  ONE_MILLISECOND,
  PRE_SCHEDULE_CARD_INTERACTIONS,
  PRE_SCHEDULE_CARD_SCREEN_EVENT,
  PRE_SCHEDULE_CARD_TAG_LABELS,
} from './PreScheduleCard.constants'
import { useFirebaseContext } from '@/modules/Firebase/contexts/FirebaseContext'

export const usePreScheduleCard = (props: PreScheduleCardProps) => {
  const { preSchedule, callbacks } = props
  const { showModal } = useModalContext()
  const { logAnalyticsEvent } = useFirebaseContext()
  const [timesToValidate, setTimesToValidate] = useState({
    isMoreThan48Hours: false,
    isLessThan48Hours: false,
    creationDateLessThan1Hour: false,
    creationDateLessThan24Hours: false,
    isExpired: false,
  })

  const preScheduleDate = useMemo(
    () => DateTime.fromFormat(preSchedule.date, 'dd/MM/yyyy HH:mm'),
    [preSchedule.date],
  )

  const preScheduleDataFormatted = useMemo(() => {
    return {
      dayOfWeek: autoCapitalize(preScheduleDate.toFormat('cccc') || ''),
      date: preScheduleDate.toFormat('dd/MM/yyyy'),
      time: preScheduleDate.toFormat('HH:mm'),
    }
  }, [preScheduleDate])

  const handleCancelPreSchedule = useCallback(() => {
    logAnalyticsEvent(PRE_SCHEDULE_CARD_SCREEN_EVENT.PAC_RESERVA_CANCELAR, {
      interaction: PRE_SCHEDULE_CARD_INTERACTIONS.CLICK,
    })
    callbacks?.onCardAnyActions()
    showModal(PRE_SCHEDULE_CONFIRMATION_CARD_MODAL_ID, {
      title: 'Deseja cancelar o pré-agendamento?',
      width: 480,
    })
  }, [callbacks, logAnalyticsEvent, showModal])

  const handleConfirmPreSchedule = useCallback(() => {
    logAnalyticsEvent(PRE_SCHEDULE_CARD_SCREEN_EVENT.PAC_RESERVA_CONFIRMAR, {
      interaction: PRE_SCHEDULE_CARD_INTERACTIONS.CLICK,
    })
    callbacks?.onCardAnyActions()
    showModal('CONFIRM_CONSULTATION', {
      width: 550,
    })
  }, [callbacks, logAnalyticsEvent, showModal])

  const handleSetTimesToValidate = useCallback(() => {
    const now = DateTime.now().setZone('America/Sao_Paulo')

    const timesValidate = {
      isExpired: preSchedule?.confirmationLimitDate < now,
      creationDateLessThan1Hour:
        preScheduleDate.diff(preSchedule?.createdAt, 'hours').hours < 1,
      creationDateLessThan24Hours:
        preScheduleDate.diff(preSchedule?.createdAt, 'hours').hours < 24,
      isMoreThan48Hours:
        preSchedule?.confirmationLimitDate.diff(now, 'hours').hours > 48,
      isLessThan48Hours:
        preSchedule?.confirmationLimitDate.diff(now, 'hours').hours <= 48,
    }

    setTimesToValidate(timesValidate)
  }, [
    preSchedule?.confirmationLimitDate,
    preSchedule?.createdAt,
    preScheduleDate,
  ])

  useEffect(() => {
    if (
      !preSchedule?.confirmationLimitDate ||
      preSchedule?.isPreScheduleConfirmed
    ) {
      return
    }

    handleSetTimesToValidate()

    const interval = setInterval(() => {
      handleSetTimesToValidate()
    }, ONE_MILLISECOND)

    return () => clearInterval(interval)
  }, [
    handleSetTimesToValidate,
    preSchedule?.confirmationLimitDate,
    preSchedule?.isPreScheduleConfirmed,
  ])

  const handleMountTagLabel: PreScheduleCardTagLabel = useMemo(() => {
    const validationActive = Object.keys(timesToValidate).find(
      key => timesToValidate[key as keyof typeof timesToValidate],
    )

    return PRE_SCHEDULE_CARD_TAG_LABELS({
      confirmationLimitDate: preSchedule?.confirmationLimitDate,
    })[validationActive]
  }, [preSchedule?.confirmationLimitDate, timesToValidate])

  const shouldDisableActions = useMemo(() => {
    return timesToValidate.isExpired
  }, [timesToValidate])

  return {
    data: {
      preSchedule: preScheduleDataFormatted,
      tag: handleMountTagLabel,
    },
    validations: {
      shouldDisableActions,
      timesToValidate,
    },
    actions: {
      handleConfirmPreSchedule,
      handleCancelPreSchedule,
      handleSetTimesToValidate,
    },
  }
}
